/**
 * BOGO same-size funnel — send shoppers to /pages/bogo-pick to choose
 * their free same-size canvas (Shopify automatic BXGY applies at checkout).
 *
 * Admin checklist (verify in Shopify → Discounts before deploy):
 * - Automatic Buy X Get Y (not a discount code)
 * - Same-size individual canvases qualify
 * - Prefer discount visible in /cart.js (line_level_discount_allocations)
 *   If BXGY only applies at checkout, _bogo line properties track funnel slots;
 *   cart prices always come from Shopify final_line_price (never overridden in theme).
 */
(function () {
  const CANVAS_SIZES = [
    'XS',
    'Standard',
    'Mid',
    'Large',
    'Large+',
    'XL',
    'XXL',
    'XXXL',
  ];

  const SIZE_COLLECTION_HANDLES = {
    XS: 'size-xs',
    Standard: 'size-standard',
    Mid: 'size-mid',
    Large: 'size-large',
    'Large+': 'size-large-plus',
    XL: 'size-xl',
    XXL: 'size-xxl',
    XXXL: 'size-xxxl',
  };

  function shopifyRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function collectionHandle() {
    return document.documentElement.dataset.catalogCollection || 'all-in-stock';
  }

  function escAttr(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  function lineProperties(item) {
    const raw = item && item.properties;
    if (!raw) return {};
    if (Array.isArray(raw)) {
      const out = {};
      raw.forEach(function (pair) {
        if (pair && pair.name != null) out[String(pair.name)] = pair.value;
      });
      return out;
    }
    return raw;
  }

  function isPackageLine(item) {
    const guard = window.GravityPackageDiscount;
    if (guard && typeof guard.isPackageCartItem === 'function') {
      return guard.isPackageCartItem(item);
    }
    const p = lineProperties(item);
    return !!(p._package || p._package_handle);
  }

  function normalizeCanvasSize(value) {
    const v = String(value || '').trim();
    if (!v) return '';
    if (CANVAS_SIZES.indexOf(v) >= 0) return v;
    const aliases = {
      L: 'Large',
      ST: 'Standard',
      M: 'Mid',
      'L+': 'Large+',
    };
    return aliases[v] || '';
  }

  function canvasSizeFromItem(item) {
    const type = normalizeCanvasSize(item.product_type);
    if (type) return type;

    const p = lineProperties(item);
    const fromProp = normalizeCanvasSize(p._canvas_size);
    if (fromProp) return fromProp;

    const vt = item.variant_title || '';
    if (vt && vt !== 'Default Title') {
      const first = normalizeCanvasSize(vt.split('/')[0].trim());
      if (first) return first;
    }

    return '';
  }

  function isBogoFreeLine(item) {
    const p = lineProperties(item);
    return p._bogo === 'free' || p._bogo_free === 'true';
  }

  /** Paid canvas added through the BOGO funnel picker (still counts as paid if Shopify discounts). */
  function isBogoFunnelPaidLine(item) {
    const p = lineProperties(item);
    return p._bogo === 'paid';
  }

  function lineFinalCents(item) {
    if (!item) return 0;
    if (typeof item.final_line_price === 'number') return item.final_line_price;
    const unit = item.final_price != null ? item.final_price : item.price;
    return (unit || 0) * (item.quantity || 1);
  }

  function lineWasCents(item) {
    if (!item) return 0;
    if (item.original_line_price) return item.original_line_price;
    if (item.line_price) return item.line_price;
    return (item.price || 0) * (item.quantity || 1);
  }

  function hasLineLevelDiscount(item) {
    const allocs = item && item.line_level_discount_allocations;
    return Array.isArray(allocs) && allocs.length > 0;
  }

  /** BXGY automatic discount or zero-priced canvas after Shopify applies the offer. */
  function isBogoDiscountedLine(item, cart) {
    if (!item || isPackageLine(item)) return false;
    if (cart && bogoPromotionsBlocked(cart)) return false;
    if (!isIndividualCanvas(item)) return false;

    const finalCents = lineFinalCents(item);
    if (finalCents > 0) return false;

    if (hasLineLevelDiscount(item)) return true;

    if (!cart || !cart.items) return false;

    const size = canvasSizeFromItem(item);
    if (!size) return false;

    const state = analyzeBogo(cart);
    const paidCount = state.counts[size] || 0;
    return paidCount >= 2;
  }

  function bogoSavingsCents(cart) {
    if (!cart || !cart.items || bogoPromotionsBlocked(cart)) return 0;
    return cart.items.reduce(function (sum, item) {
      if (!isBogoDiscountedLine(item, cart)) return sum;
      const was = lineWasCents(item);
      const final = lineFinalCents(item);
      return sum + Math.max(0, was - final);
    }, 0);
  }

  function bogoDiscountedCount(cart) {
    if (!cart || !cart.items || bogoPromotionsBlocked(cart)) return 0;
    return cart.items.filter(function (item) {
      return isBogoDiscountedLine(item, cart);
    }).length;
  }

  function isIndividualCanvas(item) {
    if (!item || isPackageLine(item)) return false;
    return !!canvasSizeFromItem(item);
  }

  function cartHasPackageLines(cart) {
    return (cart.items || []).some(isPackageLine);
  }

  function bogoPromotionsBlocked(cart) {
    const guard = window.GravityPackageDiscount;
    if (guard && typeof guard.promotionsExcludeBogo === 'function') {
      return guard.promotionsExcludeBogo(cart);
    }
    return cartHasPackageLines(cart);
  }

  function isFreeBogoPartnerLine(item) {
    if (!isIndividualCanvas(item)) return false;
    if (isBogoFunnelPaidLine(item)) return false;
    if (isBogoFreeLine(item)) return true;
    if (lineFinalCents(item) > 0) return false;
    return hasLineLevelDiscount(item);
  }

  function analyzeBogo(cart) {
    const paidCounts = {};
    const freeCounts = {};
    const lastPaidBySize = {};

    (cart.items || []).forEach(function (item) {
      if (!isIndividualCanvas(item)) return;
      const size = canvasSizeFromItem(item);
      if (!size) return;

      if (isFreeBogoPartnerLine(item)) {
        freeCounts[size] = (freeCounts[size] || 0) + (item.quantity || 1);
        return;
      }

      paidCounts[size] = (paidCounts[size] || 0) + (item.quantity || 1);
      lastPaidBySize[size] = item;
    });

    const pending = [];
    Object.keys(paidCounts).forEach(function (size) {
      const paid = paidCounts[size] || 0;
      const free = freeCounts[size] || 0;
      let owed = paid - free;
      if (owed <= 0) return;
      pending.push({
        size: size,
        count: owed,
        paidCount: paid,
        variantId: lastPaidBySize[size] ? String(lastPaidBySize[size].variant_id) : '',
      });
    });

    pending.sort(function (a, b) {
      return (paidCounts[b.size] || 0) - (paidCounts[a.size] || 0);
    });

    return {
      counts: paidCounts,
      pending: pending,
      eligible: pending.length > 0 && !bogoPromotionsBlocked(cart),
    };
  }

  function pendingSlotForSize(cart, size) {
    if (!cart || !size) return null;
    const state = analyzeBogo(cart);
    return (
      (state.pending || []).find(function (slot) {
        return slot.size === size;
      }) || null
    );
  }

  function anchorItemForSize(cart, size, urlVariantId) {
    if (!cart || !cart.items || !size) return null;

    const slot = pendingSlotForSize(cart, size);
    const pendingVariantId = slot && slot.variantId ? String(slot.variantId) : '';

    if (pendingVariantId) {
      const pendingMatch = cart.items.find(function (item) {
        return String(item.variant_id) === pendingVariantId;
      });
      if (pendingMatch) return pendingMatch;
    }

    if (urlVariantId) {
      const urlMatch = cart.items.find(function (item) {
        return String(item.variant_id) === String(urlVariantId);
      });
      if (urlMatch) return urlMatch;
    }

    let anchor = null;
    cart.items.forEach(function (item) {
      if (canvasSizeFromItem(item) !== size) return;
      anchor = item;
    });
    return anchor;
  }

  function shopUrlForSize(size) {
    const handle = SIZE_COLLECTION_HANDLES[size];
    const root = shopifyRoot();
    const base = root.endsWith('/') ? root.slice(0, -1) : root;
    if (handle) return base + '/collections/' + handle;
    return base + '/collections/' + collectionHandle();
  }

  function funnelEl() {
    return document.getElementById('bogo-funnel');
  }

  function closeChoiceModal(openBag) {
    const modal = funnelEl();
    if (!modal) return;
    modal.classList.remove('is-active', 'is-picker');
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    showFunnelStep('choice');
    closeFunnelPreview();
    modalSession = null;
    if (openBag && window.GravityCart && window.GravityCart.openCartDrawer) {
      window.GravityCart.openCartDrawer();
    }
  }

  let modalSession = null;
  let pickerAllPicks = [];
  let pickerSearchTimer = null;
  let pickerRenderToken = 0;
  let pickerFiltersBound = false;
  const PICKER_GRID_CHUNK = 8;
  const PICKER_IMAGE_WIDTH = 480;
  const pickerFilterState = {
    color: 'all',
    tex: 'all',
    q: '',
    sort: 'manual',
  };
  let funnelPreviewImages = [];
  let funnelPreviewIndex = 0;
  let funnelPreviewTitle = '';

  function escHtml(value) {
    return escAttr(value);
  }

  function funnelPreviewRoot() {
    return document.querySelector('[data-bogo-funnel-preview]');
  }

  function closeFunnelPreview() {
    const previewRoot = funnelPreviewRoot();
    if (!previewRoot) return;
    previewRoot.hidden = true;
    previewRoot.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bogo-funnel-preview-open');
    const img = previewRoot.querySelector('[data-bogo-funnel-preview-img]');
    if (img) img.src = '';
  }

  function updateFunnelPreviewDialog() {
    const previewRoot = funnelPreviewRoot();
    if (!previewRoot) return;
    const previewImg = previewRoot.querySelector('[data-bogo-funnel-preview-img]');
    const previewTitle = previewRoot.querySelector('[data-bogo-funnel-preview-title]');
    const previewCounter = previewRoot.querySelector('[data-bogo-funnel-preview-counter]');
    const previewPrev = previewRoot.querySelector('[data-bogo-funnel-preview-prev]');
    const previewNext = previewRoot.querySelector('[data-bogo-funnel-preview-next]');
    const src = funnelPreviewImages[funnelPreviewIndex] || '';
    if (previewImg) {
      if (/[?&]width=\d+/.test(src)) {
        previewImg.src = src.replace(/([?&])width=\d+/, '$1width=1400');
      } else if (src) {
        previewImg.src = src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=1400';
      }
      previewImg.alt = funnelPreviewTitle;
    }
    if (previewTitle) previewTitle.textContent = funnelPreviewTitle;
    const multi = funnelPreviewImages.length > 1;
    if (previewCounter) {
      previewCounter.hidden = !multi;
      if (multi) {
        previewCounter.textContent =
          funnelPreviewIndex + 1 + ' / ' + funnelPreviewImages.length;
      }
    }
    if (previewPrev) {
      previewPrev.hidden = !multi;
      previewPrev.disabled = funnelPreviewIndex <= 0;
    }
    if (previewNext) {
      previewNext.hidden = !multi;
      previewNext.disabled = funnelPreviewIndex >= funnelPreviewImages.length - 1;
    }
  }

  function openFunnelPreview(title, images) {
    const previewRoot = funnelPreviewRoot();
    if (!previewRoot || !images || !images.length) return;
    funnelPreviewTitle = title || '';
    funnelPreviewImages = images.slice();
    funnelPreviewIndex = 0;
    updateFunnelPreviewDialog();
    previewRoot.hidden = false;
    previewRoot.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bogo-funnel-preview-open');
  }

  function bindFunnelPreview() {
    const previewRoot = funnelPreviewRoot();
    if (!previewRoot || previewRoot.dataset.bound === 'true') return;
    previewRoot.dataset.bound = 'true';
    previewRoot.querySelectorAll('[data-bogo-funnel-preview-close]').forEach(function (el) {
      el.addEventListener('click', closeFunnelPreview);
    });
    const previewPrev = previewRoot.querySelector('[data-bogo-funnel-preview-prev]');
    const previewNext = previewRoot.querySelector('[data-bogo-funnel-preview-next]');
    if (previewPrev) {
      previewPrev.addEventListener('click', function () {
        if (funnelPreviewIndex <= 0) return;
        funnelPreviewIndex -= 1;
        updateFunnelPreviewDialog();
      });
    }
    if (previewNext) {
      previewNext.addEventListener('click', function () {
        if (funnelPreviewIndex >= funnelPreviewImages.length - 1) return;
        funnelPreviewIndex += 1;
        updateFunnelPreviewDialog();
      });
    }
  }

  function showFunnelStep(step) {
    const modal = funnelEl();
    const choice = document.getElementById('bogo-funnel-choice');
    const picker = document.getElementById('bogo-funnel-picker');
    const tiers = document.getElementById('bogo-funnel-tiers');
    const isPicker = step === 'picker';
    if (modal) modal.classList.toggle('is-picker', isPicker);
    if (choice) choice.hidden = isPicker;
    if (picker) picker.hidden = !isPicker;
    if (tiers) tiers.hidden = isPicker;
  }

  function thumbImageUrl(url, width) {
    const w = width || PICKER_IMAGE_WIDTH;
    if (!url) return '';
    if (/[?&]width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, '$1width=' + w);
    }
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }

  function productImagesFromJson(product) {
    return (product.images || [])
      .map(function (img) {
        return img && img.src ? img.src : '';
      })
      .filter(Boolean);
  }

  function getProductOption(product, name) {
    const options = product.options || [];
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      if (opt && opt.name === name && opt.values && opt.values[0]) {
        return opt.values[0];
      }
    }
    return '';
  }

  function productSizeFromJson(product) {
    const type = normalizeCanvasSize(product.product_type);
    if (type) return type;
    const options = product.options || [];
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      if (opt && opt.name === 'Size' && opt.values && opt.values[0]) {
        return normalizeCanvasSize(opt.values[0]);
      }
    }
    const variant = product.variants && product.variants[0];
    if (variant && variant.title && variant.title !== 'Default Title') {
      return normalizeCanvasSize(variant.title.split('/')[0].trim());
    }
    return type;
  }

  async function fetchCollectionProducts(handle, onProgress) {
    const base = shopifyRoot().replace(/\/$/, '');
    const products = [];
    let page = 1;

    while (page <= 12) {
      const url =
        base +
        '/collections/' +
        encodeURIComponent(handle) +
        '/products.json?limit=250&page=' +
        page;
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        if (page === 1) throw new Error('Could not load canvases');
        break;
      }
      const data = await res.json();
      const batch = data.products || [];
      if (!batch.length) break;
      products.push.apply(products, batch);
      if (typeof onProgress === 'function') {
        onProgress({ page: page, count: products.length });
      }
      if (batch.length < 250) break;
      page += 1;
    }
    return products;
  }

  function productIdsInCart(cart) {
    const ids = new Set();
    (cart.items || []).forEach(function (item) {
      if (item.product_id != null) ids.add(String(item.product_id));
    });
    return ids;
  }

  function setPickerLoadingProgress(percent, message) {
    const statusEl = document.querySelector('[data-bogo-funnel-loading-status]');
    const barEl = document.querySelector('[data-bogo-funnel-loading-bar]');
    if (statusEl && message) statusEl.textContent = message;
    if (barEl) {
      barEl.style.width = Math.min(100, Math.max(0, percent)) + '%';
    }
  }

  function setPickerLoadingActive(active) {
    const choicesWrap = document.querySelector('[data-bogo-funnel-choices]');
    const loadingEl = document.querySelector('[data-bogo-funnel-loading]');
    if (choicesWrap) {
      choicesWrap.classList.toggle('is-loading-choices', active);
      choicesWrap.setAttribute('aria-busy', active ? 'true' : 'false');
    }
    if (loadingEl) loadingEl.hidden = !active;
  }

  function pickerMatches(pick) {
    const opts = window.GRAVITY_FILTER_OPTIONS;
    if (opts && opts.colorMatchesFilter) {
      if (!opts.colorMatchesFilter(pick.color, pickerFilterState.color)) return false;
    } else if (pickerFilterState.color !== 'all' && pick.color !== pickerFilterState.color) {
      return false;
    }
    if (pickerFilterState.tex !== 'all' && pick.tex !== pickerFilterState.tex) {
      return false;
    }
    if (pickerFilterState.q) {
      const hay = [pick.code, pick.title, pick.color, pick.tex]
        .join(' ')
        .toLowerCase();
      if (hay.indexOf(pickerFilterState.q.toLowerCase()) === -1) return false;
    }
    return true;
  }

  function sortPickerPicks(list) {
    const sorted = list.slice();
    if (pickerFilterState.sort === 'title-ascending') {
      sorted.sort(function (a, b) {
        return a.title.localeCompare(b.title);
      });
    } else if (pickerFilterState.sort === 'title-descending') {
      sorted.sort(function (a, b) {
        return b.title.localeCompare(a.title);
      });
    } else if (pickerFilterState.sort === 'created-descending') {
      sorted.sort(function (a, b) {
        return String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
      });
    }
    return sorted;
  }

  function syncPickerFilterChips() {
    const filtersEl = document.querySelector('[data-bogo-funnel-filters]');
    if (!filtersEl) return;
    filtersEl.querySelectorAll('.gravity-shop__chip').forEach(function (chip) {
      const key = chip.getAttribute('data-filter-key');
      const val = chip.getAttribute('data-filter-val');
      chip.setAttribute('aria-pressed', pickerFilterState[key] === val ? 'true' : 'false');
    });
  }

  function updatePickerFilterCount(visible, total) {
    const countEl = document.querySelector('[data-bogo-funnel-count]');
    if (!countEl || !modalSession) return;
    const noun = visible === 1 ? 'canvas' : 'canvases';
    const size = modalSession.slot.size;
    if (visible === total) {
      countEl.textContent = visible + ' ' + size + ' ' + noun;
    } else {
      countEl.textContent = visible + ' of ' + total + ' ' + size + ' ' + noun;
    }
  }

  function renderPickerCard(pick, size) {
    const card = document.createElement('article');
    const thumbSrc = thumbImageUrl(pick.image, PICKER_IMAGE_WIDTH);
    card.className = 'bogo-pick-page__card';
    card.innerHTML =
      '<div class="bogo-pick-page__card-media">' +
      (thumbSrc
        ? '<img src="' +
          escAttr(thumbSrc) +
          '" alt="" loading="lazy" decoding="async" fetchpriority="low" width="240" height="320">'
        : '') +
      '<span class="bogo-pick-page__card-badge bogo-pick-page__card-badge--paid">Same size</span>' +
      (pick.image
        ? '<button type="button" class="bogo-pick-page__preview-btn" data-bogo-funnel-preview-btn>Quick look</button>'
        : '') +
      '</div>' +
      '<div class="bogo-pick-page__card-body">' +
      '<p class="bogo-pick-page__card-name">' +
      escHtml(pick.title) +
      '</p>' +
      '<button type="button" class="btn btn-primary bogo-pick-page__card-add">Add this canvas</button>' +
      '</div>';

    const previewBtn = card.querySelector('[data-bogo-funnel-preview-btn]');
    if (previewBtn) {
      previewBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        openFunnelPreview(
          pick.title,
          pick.images && pick.images.length ? pick.images : [pick.image]
        );
      });
    }

    const addBtn = card.querySelector('.bogo-pick-page__card-add');
    if (addBtn) {
      addBtn.addEventListener('click', async function () {
        addBtn.disabled = true;
        card.classList.add('is-loading');
        try {
          if (!window.GravityCart || !window.GravityCart.addVariant) {
            throw new Error('Cart is unavailable');
          }
          const previousSlot =
            modalSession && modalSession.slot ? Object.assign({}, modalSession.slot) : null;

          const result = await window.GravityCart.addVariant(pick.variantId, 1, {
            skipBogoPrompt: true,
            skipDrawer: true,
            isCanvas: true,
            properties: {
              _canvas_size: size,
              _bogo: 'paid',
            },
          });

          if (result && result.duplicate) {
            alert('This backdrop is already in your bag. Pick a different canvas.');
            addBtn.disabled = false;
            card.classList.remove('is-loading');
            return;
          }

          let cartAfter = null;
          if (window.GravityCart.fetchCartWithBundles) {
            cartAfter = await window.GravityCart.fetchCartWithBundles();
          } else if (window.GravityCart.fetchCart) {
            cartAfter = await window.GravityCart.fetchCart();
          }
          if (!cartAfter) throw new Error('Could not refresh your bag');

          advanceAfterPaidCanvasAdd(cartAfter, previousSlot, size);
        } catch (err) {
          alert(err.message || 'Could not add canvas.');
          addBtn.disabled = false;
          card.classList.remove('is-loading');
        }
      });
    }

    return card;
  }

  function renderPickerGridChunk(filtered, start, token) {
    const gridEl = document.querySelector('[data-bogo-funnel-grid]');
    if (!gridEl || token !== pickerRenderToken || !modalSession) return;
    const end = Math.min(start + PICKER_GRID_CHUNK, filtered.length);
    for (let i = start; i < end; i += 1) {
      gridEl.appendChild(renderPickerCard(filtered[i], modalSession.slot.size));
    }
    if (end < filtered.length) {
      requestAnimationFrame(function () {
        renderPickerGridChunk(filtered, end, token);
      });
    }
  }

  function renderPickerGrid() {
    const gridEl = document.querySelector('[data-bogo-funnel-grid]');
    if (!gridEl) return;
    const filtered = sortPickerPicks(pickerAllPicks.filter(pickerMatches));
    syncPickerFilterChips();
    updatePickerFilterCount(filtered.length, pickerAllPicks.length);

    pickerRenderToken += 1;
    const token = pickerRenderToken;
    gridEl.innerHTML = '';
    if (!filtered.length) {
      gridEl.innerHTML =
        '<div class="bogo-pick-page__filter-empty">' +
        '<h3>No canvases match.</h3>' +
        '<p>Try clearing a filter — every piece is one of a kind.</p>' +
        '</div>';
      return;
    }
    renderPickerGridChunk(filtered, 0, token);
  }

  function initPickerFilterChips() {
    const opts = window.GRAVITY_FILTER_OPTIONS;
    const filtersEl = document.querySelector('[data-bogo-funnel-filters]');
    if (!opts || !filtersEl) return;
    opts.renderChipRow(
      filtersEl.querySelector('[data-chip-row="color"]'),
      'color',
      opts.colors,
      pickerFilterState.color
    );
    opts.renderChipRow(
      filtersEl.querySelector('[data-chip-row="tex"]'),
      'tex',
      opts.textures,
      pickerFilterState.tex
    );
  }

  function bindPickerFilters() {
    if (pickerFiltersBound) return;
    pickerFiltersBound = true;
    const filtersEl = document.querySelector('[data-bogo-funnel-filters]');
    const searchInput = document.querySelector('[data-bogo-funnel-search]');
    const sortSelect = document.querySelector('[data-bogo-funnel-sort]');
    const clearBtn = document.querySelector('[data-bogo-funnel-clear]');
    if (!filtersEl) return;

    initPickerFilterChips();

    filtersEl.addEventListener('click', function (e) {
      const chip = e.target.closest('.gravity-shop__chip');
      if (!chip) return;
      const key = chip.getAttribute('data-filter-key');
      const val = chip.getAttribute('data-filter-val');
      if (!key) return;
      pickerFilterState[key] = val;
      renderPickerGrid();
    });

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        pickerFilterState.q = searchInput.value.trim();
        clearTimeout(pickerSearchTimer);
        pickerSearchTimer = setTimeout(renderPickerGrid, 280);
      });
    }

    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        pickerFilterState.sort = sortSelect.value;
        renderPickerGrid();
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        pickerFilterState.color = 'all';
        pickerFilterState.tex = 'all';
        pickerFilterState.q = '';
        pickerFilterState.sort = 'manual';
        if (searchInput) searchInput.value = '';
        if (sortSelect) sortSelect.value = 'manual';
        initPickerFilterChips();
        renderPickerGrid();
      });
    }
  }

  function updatePickerCallout(slot) {
    const titleEl = document.querySelector('[data-bogo-funnel-picker-callout-title]');
    const subEl = document.querySelector('[data-bogo-funnel-picker-callout-sub]');
    if (titleEl) {
      titleEl.textContent = 'Choose your next ' + slot.size + ' canvas';
    }
    if (subEl) {
      subEl.textContent =
        'Add one more paid ' +
        slot.size +
        ' backdrop to unlock 2 free at checkout. Filter, quick view, then add to your bag.';
    }
  }

  async function loadPickerCatalog(cart, slot) {
    const emptyEl = document.querySelector('[data-bogo-funnel-picker-empty]');
    const filtersEl = document.querySelector('[data-bogo-funnel-filters]');
    const gridEl = document.querySelector('[data-bogo-funnel-grid]');
    if (emptyEl) {
      emptyEl.hidden = true;
      emptyEl.textContent = '';
    }
    if (filtersEl) filtersEl.hidden = true;
    if (gridEl) gridEl.innerHTML = '';

    pickerFilterState.color = 'all';
    pickerFilterState.tex = 'all';
    pickerFilterState.q = '';
    pickerFilterState.sort = 'manual';
    const searchInput = document.querySelector('[data-bogo-funnel-search]');
    const sortSelect = document.querySelector('[data-bogo-funnel-sort]');
    if (searchInput) searchInput.value = '';
    if (sortSelect) sortSelect.value = 'manual';

    setPickerLoadingActive(true);
    setPickerLoadingProgress(12, 'Checking your bag…');

    const handle = SIZE_COLLECTION_HANDLES[slot.size] || collectionHandle();
    const productsPromise = fetchCollectionProducts(handle, function (progress) {
      const pct = Math.min(88, 20 + progress.page * 14);
      setPickerLoadingProgress(
        pct,
        progress.page === 1
          ? 'Loading ' + slot.size + ' canvases…'
          : 'Still loading — page ' + progress.page + ' of the catalogue…'
      );
    });

    const inCartProducts = cart ? productIdsInCart(cart) : new Set();
    let products = [];
    try {
      products = await productsPromise;
      setPickerLoadingProgress(92, 'Almost ready…');
    } catch (err) {
      setPickerLoadingActive(false);
      if (emptyEl) {
        emptyEl.hidden = false;
        emptyEl.textContent = err.message || 'Could not load canvases.';
      }
      return;
    }

    const picks = [];
    products.forEach(function (product) {
      if (productSizeFromJson(product) !== slot.size) return;
      if (!product.variants || !product.variants.length) return;
      if (inCartProducts.has(String(product.id))) return;

      const variant = product.variants.find(function (v) {
        return v.available;
      });
      if (!variant) return;

      const images = productImagesFromJson(product);
      const colorRaw = getProductOption(product, 'Color') || '';
      const texRaw = getProductOption(product, 'Texture') || '';
      picks.push({
        id: product.id,
        title: product.title,
        code: (product.title || '').split(' ')[0] || '',
        color: colorRaw.toLowerCase(),
        tex: texRaw.replace(/ texture$/i, '').trim(),
        createdAt: product.created_at || '',
        image: images[0] || '',
        images: images,
        variantId: variant.id,
      });
    });

    pickerAllPicks = picks;
    setPickerLoadingProgress(100, 'Ready');
    setPickerLoadingActive(false);

    if (!picks.length) {
      if (emptyEl) {
        emptyEl.hidden = false;
        emptyEl.innerHTML =
          'No other in-stock ' +
          escHtml(slot.size) +
          ' canvases are available right now. ' +
          '<a href="' +
          escAttr(shopUrlForSize(slot.size)) +
          '">Browse the full catalogue</a>.';
      }
      return;
    }

    if (filtersEl) filtersEl.hidden = false;
    bindPickerFilters();
    initPickerFilterChips();
    renderPickerGrid();
  }

  function openAddPaidPicker(cart, slot, context) {
    if (!slot || slot.paidCount !== 1) {
      if (slot && slot.count >= 1) {
        goPickSecondCanvas(slot.size, context || {}, slot.variantId);
      }
      return false;
    }

    const modal = funnelEl();
    if (!modal || !slot) {
      window.location.href = shopUrlForSize(slot.size);
      return false;
    }

    modalSession = { cart: cart, slot: slot, context: context || { reason: 'add' } };
    updatePickerCallout(slot);
    showFunnelStep('picker');

    const titleEl = document.getElementById('bogo-funnel-title');
    const leadEl = document.getElementById('bogo-funnel-lead');
    if (titleEl) {
      titleEl.textContent = 'Add another ' + slot.size + ' canvas';
    }
    if (leadEl) {
      leadEl.textContent =
        'Pick the backdrop you want to add — you will unlock 2 free ' +
        slot.size +
        ' canvases.';
    }

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-active', 'is-picker');
    document.body.style.overflow = 'hidden';
    if (window.GravityCart && window.GravityCart.closeCartDrawer) {
      window.GravityCart.closeCartDrawer();
    }

    loadPickerCatalog(cart, slot);
    return true;
  }

  const UPSELL_COPY = {
    first: {
      title: 'You unlocked 1 free canvas',
      tierHighlight: 0,
      lead: function (size) {
        return (
          'Add another ' +
          size +
          ' canvas and get 2 free instead of 1. Or pick your free one now.'
        );
      },
      pickLabel: function (size) {
        return 'Pick my free ' + size + ' canvas';
      },
      addPaidLabel: function (size) {
        return 'Add another ' + size + ' canvas (2 free)';
      },
    },
  };

  /** Only upsell from 1 paid → 2 paid. Max 2 free per size — no further stacking. */
  function upsellModalState(slot) {
    if (!slot || slot.count < 1) return null;
    if (slot.paidCount === 1 && slot.count === 1) return 'first';
    return null;
  }

  function lastPaidVariantIdForSize(cart, size) {
    if (!cart || !cart.items || !size) return '';
    let variantId = '';
    cart.items.forEach(function (item) {
      if (canvasSizeFromItem(item) !== size) return;
      if (isFreeBogoPartnerLine(item)) return;
      variantId = String(item.variant_id);
    });
    return variantId;
  }

  function pendingSlotForSizeFromCart(cart, size) {
    if (!cart || !size) return null;
    const state = analyzeBogo(cart);
    return (
      (state.pending || []).find(function (slot) {
        return slot.size === size;
      }) || null
    );
  }

  function syntheticSlotForTier(size, paidCount, cart) {
    return {
      size: size,
      paidCount: paidCount,
      count: paidCount,
      variantId: lastPaidVariantIdForSize(cart, size),
    };
  }

  function advanceAfterPaidCanvasAdd(cart, previousSlot, size) {
    const context = (modalSession && modalSession.context) || { reason: 'add' };
    const prevPaid = previousSlot && previousSlot.paidCount ? previousSlot.paidCount : 0;
    let slot = pendingSlotForSizeFromCart(cart, size);

    if (!slot && prevPaid === 1) {
      slot = syntheticSlotForTier(size, 2, cart);
    }

    if (!slot || slot.count < 1) {
      goPickSecondCanvas(size, context, lastPaidVariantIdForSize(cart, size));
      return;
    }

    const upsellState = upsellModalState(slot);
    if (upsellState) {
      openUpsellModal(cart, slot, upsellState, context);
      return;
    }

    goPickSecondCanvas(size, context, slot.variantId);
  }

  function renderUpsellTiers(tierHighlight) {
    const tiersEl = document.getElementById('bogo-funnel-tiers');
    if (!tiersEl) return;
    tiersEl.innerHTML =
      '<li class="' +
      (tierHighlight === 0 ? 'is-active' : '') +
      '">Buy 1 canvas → get 1 free (same size)</li>' +
      '<li class="' +
      (tierHighlight === 1 ? 'is-active' : '') +
      '">Buy 2 canvases → get 2 free (same size)</li>';
  }

  function openUpsellModal(cart, slot, state, context) {
    const modal = funnelEl();
    if (!modal) {
      goPickSecondCanvas(slot.size, context, slot.variantId);
      return true;
    }

    const copy = UPSELL_COPY[state];
    if (!copy) {
      goPickSecondCanvas(slot.size, context, slot.variantId);
      return true;
    }

    const titleEl = document.getElementById('bogo-funnel-title');
    const leadEl = document.getElementById('bogo-funnel-lead');
    const addPaidBtn = document.getElementById('bogo-funnel-add-paid');
    const pickFreeBtn = document.getElementById('bogo-funnel-pick-free');

    if (titleEl) {
      titleEl.textContent = copy.title;
    }
    if (leadEl) {
      leadEl.textContent = copy.lead(slot.size);
    }
    if (addPaidBtn) {
      addPaidBtn.textContent = copy.addPaidLabel(slot.size);
    }
    if (pickFreeBtn) {
      pickFreeBtn.href = pickPageUrl(slot.size, slot.variantId, context);
      pickFreeBtn.textContent = copy.pickLabel(slot.size);
    }

    modalSession = { cart: cart, slot: slot, context: context || { reason: 'add' } };
    showFunnelStep('choice');

    renderUpsellTiers(copy.tierHighlight);

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    if (window.GravityCart && window.GravityCart.closeCartDrawer) {
      window.GravityCart.closeCartDrawer();
    }
    return true;
  }

  function openChoiceModal(cart, slot, context) {
    const state = upsellModalState(slot);
    if (!state) {
      goPickSecondCanvas(slot.size, context, slot.variantId);
      return false;
    }
    return openUpsellModal(cart, slot, state, context);
  }

  function clearCheckoutSkip() {
    try {
      sessionStorage.removeItem('gravity_bogo_checkout_skip');
    } catch (e) {
      /* ignore */
    }
  }

  function setCheckoutSkip() {
    try {
      sessionStorage.setItem('gravity_bogo_checkout_skip', '1');
    } catch (e) {
      /* ignore */
    }
  }

  function shouldSkipCheckoutPrompt() {
    try {
      return sessionStorage.getItem('gravity_bogo_checkout_skip') === '1';
    } catch (e) {
      return false;
    }
  }

  function pickPageUrl(size, variantId, context) {
    const root = shopifyRoot();
    const base = root.endsWith('/') ? root.slice(0, -1) : root;
    let url =
      base +
      '/pages/bogo-pick?size=' +
      encodeURIComponent(size);
    if (variantId) {
      url += '&variant_id=' + encodeURIComponent(variantId);
    }
    if (context && context.reason === 'checkout') {
      url += '&bogo_return=checkout';
    }
    return url;
  }

  function goPickSecondCanvas(size, context, variantId) {
    if (window.GravityCart && window.GravityCart.closeCartDrawer) {
      window.GravityCart.closeCartDrawer();
    }
    clearCheckoutSkip();
    window.location.href = pickPageUrl(size, variantId, context);
  }

  function goToBogoPick(cart, context) {
    const state = analyzeBogo(cart);
    if (!state.eligible || !state.pending.length) return false;
    const slot = state.pending[0];
    goPickSecondCanvas(
      slot.size,
      context || { reason: 'strip' },
      slot.variantId
    );
    return true;
  }

  async function maybePrompt(cart, context) {
    if (!cart || !cart.item_count) return false;
    if (bogoPromotionsBlocked(cart)) return false;

    const state = analyzeBogo(cart);
    if (!state.eligible || !state.pending.length) return false;

    if (context && context.reason === 'checkout' && shouldSkipCheckoutPrompt()) {
      return false;
    }

    const slot = state.pending[0];
    const upsellState =
      context && context.reason === 'add' ? upsellModalState(slot) : null;

    if (upsellState) {
      return openUpsellModal(cart, slot, upsellState, context);
    }

    goPickSecondCanvas(slot.size, context || {}, slot.variantId);
    return true;
  }

  function upsellStripLine(slot) {
    if (slot.paidCount === 1 && slot.count === 1) {
      return ' Add another ' + slot.size + ' canvas to unlock 2 free instead of 1.';
    }
    return '';
  }

  function upsellStripLinkHtml(slot) {
    if (slot.paidCount === 1 && slot.count === 1) {
      return (
        '<button type="button" class="cart-bogo-strip__link" data-bogo-strip-add-paid>' +
        'Add another ' +
        escAttr(slot.size) +
        ' canvas (2 free)</button>'
      );
    }
    return '';
  }

  function renderStrip(cart, strip) {
    if (!strip) return;
    const state = analyzeBogo(cart);

    if (!state.eligible || !state.pending.length) {
      strip.hidden = true;
      strip.innerHTML = '';
      return;
    }

    const slot = state.pending[0];
    const owed = slot.count || 1;
    const pickUrl = pickPageUrl(slot.size, slot.variantId, { reason: 'strip' });
    const pickLabel =
      owed > 1
        ? 'Pick ' + owed + ' free ' + slot.size + ' canvases'
        : 'Pick free ' + slot.size + ' canvas';
    const upsellLine = upsellStripLine(slot);

    strip.hidden = false;
    strip.innerHTML =
      '<span class="cart-bogo-strip__badge">BOGO available</span>' +
      '<p class="cart-bogo-strip__title">' +
      (owed > 1 ? 'Pick your ' + owed + ' free canvases' : 'Pick your free canvas') +
      '</p>' +
      '<p class="cart-bogo-strip__text">You have ' +
      slot.paidCount +
      ' ' +
      escAttr(slot.size) +
      ' canvas' +
      (slot.paidCount === 1 ? '' : 'es') +
      ' in your bag — choose ' +
      (owed > 1 ? owed + ' free same-size backdrops' : 'a second same-size backdrop for free') +
      '.' +
      escAttr(upsellLine) +
      '</p>' +
      '<a class="btn btn-primary cart-bogo-strip__btn" href="' +
      escAttr(pickUrl) +
      '"><span class="cart-bogo-strip__btn-text">' +
      escAttr(pickLabel) +
      '</span></a>' +
      upsellStripLinkHtml(slot);
  }

  function bindEvents() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-bogo-close]')) {
        e.preventDefault();
        closeChoiceModal(true);
        return;
      }

      if (e.target.closest('#bogo-funnel-add-paid')) {
        e.preventDefault();
        if (!modalSession || !modalSession.slot) return;
        openAddPaidPicker(
          modalSession.cart,
          modalSession.slot,
          modalSession.context
        );
        return;
      }

      if (e.target.closest('[data-bogo-strip-add-paid]')) {
        e.preventDefault();
        if (!window.GravityCart || !window.GravityCart.fetchCart) return;
        const btn = e.target.closest('[data-bogo-strip-add-paid]');
        if (btn) btn.disabled = true;
        window.GravityCart.fetchCart()
          .then(function (cart) {
            const state = analyzeBogo(cart);
            if (!state.eligible || !state.pending.length) return;
            openAddPaidPicker(cart, state.pending[0], { reason: 'strip' });
          })
          .finally(function () {
            if (btn) btn.disabled = false;
          });
        return;
      }

      if (e.target.closest('[data-bogo-picker-back]')) {
        e.preventDefault();
        if (!modalSession) {
          closeChoiceModal(false);
          return;
        }
        const upsellState = upsellModalState(modalSession.slot);
        if (upsellState) {
          openUpsellModal(
            modalSession.cart,
            modalSession.slot,
            upsellState,
            modalSession.context
          );
        } else {
          showFunnelStep('choice');
        }
        return;
      }

      if (e.target.closest('[data-bogo-open]')) {
        e.preventDefault();
        if (!window.GravityCart || !window.GravityCart.fetchCart) return;
        const btn = e.target.closest('[data-bogo-open]');
        if (btn) btn.disabled = true;
        window.GravityCart.fetchCart()
          .then(function (cart) {
            goToBogoPick(cart, { reason: 'strip' });
          })
          .finally(function () {
            if (btn) btn.disabled = false;
          });
        return;
      }

      if (e.target.closest('[data-bogo-skip-checkout]')) {
        e.preventDefault();
        setCheckoutSkip();
        window.location.href = shopifyRoot() + 'checkout';
      }
    });
  }

  window.GravityBogoFunnel = {
    analyzeBogo: analyzeBogo,
    pendingSlotForSize: pendingSlotForSize,
    anchorItemForSize: anchorItemForSize,
    isBogoFreeLine: isBogoFreeLine,
    isBogoFunnelPaidLine: isBogoFunnelPaidLine,
    isBogoDiscountedLine: isBogoDiscountedLine,
    bogoPromotionsBlocked: bogoPromotionsBlocked,
    bogoSavingsCents: bogoSavingsCents,
    bogoDiscountedCount: bogoDiscountedCount,
    lineWasCents: lineWasCents,
    canvasSizeFromItem: canvasSizeFromItem,
    isIndividualCanvas: isIndividualCanvas,
    maybePrompt: maybePrompt,
    renderStrip: renderStrip,
    clearCheckoutSkip: clearCheckoutSkip,
    pickPageUrl: pickPageUrl,
    shopUrlForSize: shopUrlForSize,
    upsellModalState: upsellModalState,
    openUpsellModal: openUpsellModal,
    openChoiceModal: openChoiceModal,
    openAddPaidPicker: openAddPaidPicker,
    closeChoiceModal: closeChoiceModal,
  };

  function init() {
    bindEvents();
    bindFunnelPreview();
    document.addEventListener('keydown', function (e) {
      const previewRoot = funnelPreviewRoot();
      if (
        previewRoot &&
        !previewRoot.hidden &&
        e.key === 'Escape'
      ) {
        closeFunnelPreview();
        return;
      }
      if (e.key !== 'Escape') return;
      const modal = funnelEl();
      if (modal && modal.classList.contains('is-active')) {
        closeChoiceModal(true);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
